import { inject, Injectable } from '@angular/core';
import { AuthStore } from '@core/store/core/auth.store';

type FeatureAction = 'list' | 'export' | 'create' | 'update' | 'delete' | 'summarize' | 'update_legal_hold';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly authStore = inject(AuthStore);

  /**
   * Check if user has a specific permission.
   * Looks up the resource in both navFeatures and featuresByResource.
   * @param permission Permission string in format "resource:action" (e.g., "user:list")
   * @returns true if user has the permission, false otherwise
   *
   * @example
   * permissionService.hasPermission('user:list')
   * permissionService.hasPermission('department:create')
   */
  hasPermission(permission: string): boolean {
    const [resource, action] = permission.split(':');

    if (!resource || !action) {
      console.warn(`Invalid permission format: "${permission}". Expected format: "resource:action"`);
      return false;
    }

    const actions = this.authStore.getResourceActions(resource);

    if (!actions) {
      return false;
    }

    return actions.includes(action as FeatureAction);
  }

  /**
   * Check if user has any of the given permissions.
   * @param permissions Array of permission strings in format "resource:action"
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  /**
   * Check if user can list a specific resource
   * @param resource Resource name (e.g., "user", "department")
   */
  canList(resource: string): boolean {
    return this.hasPermission(`${resource}:list`);
  }

  /**
   * Check if user can list any of the given resources
   * @param resources Array of resource names (e.g., ["user", "department"])
   */
  canListAny(resources: string[]): boolean {
    return resources.some((resource) => this.hasPermission(`${resource}:list`));
  }

  /**
   * Check if user can export a specific resource
   * @param resource Resource name (e.g., "user", "department")
   */
  canExport(resource: string): boolean {
    return this.hasPermission(`${resource}:export`);
  }

  /**
   * Check if user can create a specific resource
   * @param resource Resource name (e.g., "user", "department")
   */
  canCreate(resource: string): boolean {
    return this.hasPermission(`${resource}:create`);
  }

  /**
   * Check if user can update a specific resource
   * @param resource Resource name (e.g., "user", "department")
   */
  canUpdate(resource: string): boolean {
    return this.hasPermission(`${resource}:update`);
  }

  /**
   * Check if user can delete a specific resource
   * @param resource Resource name (e.g., "user", "department")
   */
  canDelete(resource: string): boolean {
    return this.hasPermission(`${resource}:delete`);
  }

  /**
   * Check if user can summarize a specific resource
   * @param resource Resource name (e.g., "user", "department")
   */
  canSummarize(resource: string): boolean {
    return this.hasPermission(`${resource}:summarize`);
  }
}
